import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import patientService from '../../../../services/patient.service';
import { showError, showSuccess } from '../../../../utils/ecxSwal';

export const THANK_YOU_TEMPLATES = [
    {
        value: 'THANK_YOU',
        label: 'General Thank You',
        description: 'Post-visit thank you with optional feedback link.',
        icon: 'bi-heart',
    },
    {
        value: 'THANK_YOU_POST_EYE_EXAM',
        label: 'Post Eye Exam',
        description: 'Thank you after an eye exam with trust and referral message.',
        icon: 'bi-eye',
    },
    {
        value: 'THANK_YOU_LENS_PURCHASE',
        label: 'Lens Purchase',
        description: 'Contact lens care instructions and referral note.',
        icon: 'bi-droplet',
    },
    {
        value: 'THANK_YOU_GLASS_PURCHASE',
        label: 'Glass Purchase',
        description: 'Glasses adjustment tips, care instructions, and referral note.',
        icon: 'bi-eyeglasses',
    },
];

function formatDisplayPhone(cellNumber) {
    if (!cellNumber) {
        return '';
    }

    const digits = cellNumber.replace(/\D/g, '');
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return cellNumber;
}

function SendPatientThankYouModal({ show, onClose, clinicId, patient, channel = 'email', initialTemplateType = 'THANK_YOU' }) {
    const isSms = channel === 'sms';
    const [selectedTemplate, setSelectedTemplate] = useState(initialTemplateType);
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewMessage, setPreviewMessage] = useState('');
    const [previewSubject, setPreviewSubject] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const patientLabel = useMemo(() => {
        if (!patient) {
            return '';
        }

        const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ');
        return patient.preferredName ? `${name} (${patient.preferredName})` : name;
    }, [patient]);

    const recipientLabel = useMemo(() => {
        if (!patient) {
            return '';
        }

        return isSms ? formatDisplayPhone(patient.cellNumber) : patient.email;
    }, [patient, isSms]);

    const selectedTemplateMeta = useMemo(
        () => THANK_YOU_TEMPLATES.find((template) => template.value === selectedTemplate),
        [selectedTemplate]
    );

    useEffect(() => {
        if (show && patient) {
            setSelectedTemplate(initialTemplateType);
            setPreviewHtml('');
            setPreviewMessage('');
            setPreviewSubject('');
            setShowPreview(false);
        }
    }, [show, patient, initialTemplateType, channel]);

    const buildPayload = () => ({
        reminderType: selectedTemplate,
    });

    const handlePreview = async () => {
        if (!clinicId || !patient?.id || !selectedTemplate) {
            return;
        }

        setPreviewLoading(true);
        setShowPreview(true);

        try {
            if (isSms) {
                const res = await patientService.previewPatientThankYouSms(clinicId, patient.id, buildPayload());
                setPreviewMessage(res.data.message || '');
                setPreviewHtml('');
                setPreviewSubject('');
            } else {
                const res = await patientService.previewPatientThankYouEmail(clinicId, patient.id, buildPayload());
                setPreviewHtml(res.data.html || '');
                setPreviewSubject(res.data.subject || '');
                setPreviewMessage('');
            }
        } catch (error) {
            console.error('Failed to preview thank you message', error);
            showError({
                title: 'Preview Failed',
                text: error?.response?.data?.errorMessage || 'Could not generate the thank you preview.',
            });
            setShowPreview(false);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSend = async () => {
        if (!clinicId || !patient?.id || !selectedTemplate) {
            return;
        }

        setSending(true);

        try {
            if (isSms) {
                await patientService.sendPatientThankYouSms(clinicId, patient.id, buildPayload());
                showSuccess({
                    title: 'SMS Queued',
                    text: `Thank you SMS to ${recipientLabel} has been queued for delivery.`,
                });
            } else {
                await patientService.sendPatientThankYouEmail(clinicId, patient.id, buildPayload());
                showSuccess({
                    title: 'Email Queued',
                    text: `Thank you email to ${recipientLabel} has been queued for delivery.`,
                });
            }
            onClose();
        } catch (error) {
            console.error('Failed to send thank you message', error);
            showError({
                title: 'Send Failed',
                text: error?.response?.data?.errorMessage || 'Could not queue the thank you message for sending.',
            });
        } finally {
            setSending(false);
        }
    };

    if (!patient) {
        return null;
    }

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            size="lg"
            scrollable
            backdrop="static"
            className={isSms ? 'patient-send-sms-modal' : 'patient-send-email-modal'}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className={`bi ${isSms ? 'bi-chat-dots' : 'bi-heart'} me-2`} />
                    Send Thank You {isSms ? 'SMS' : 'Email'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className={`${isSms ? 'patient-send-sms-modal__meta' : 'patient-send-email-modal__meta'} mb-3`}>
                    <div className={isSms ? 'patient-send-sms-modal__meta-item' : 'patient-send-email-modal__meta-item'}>
                        <span className={isSms ? 'patient-send-sms-modal__meta-label' : 'patient-send-email-modal__meta-label'}>
                            Patient
                        </span>
                        <span className={isSms ? 'patient-send-sms-modal__meta-value' : 'patient-send-email-modal__meta-value'}>
                            {patientLabel}
                        </span>
                    </div>
                    <div className={isSms ? 'patient-send-sms-modal__meta-item' : 'patient-send-email-modal__meta-item'}>
                        <span className={isSms ? 'patient-send-sms-modal__meta-label' : 'patient-send-email-modal__meta-label'}>
                            To
                        </span>
                        <span className={isSms ? 'patient-send-sms-modal__meta-value' : 'patient-send-email-modal__meta-value'}>
                            {recipientLabel}
                        </span>
                    </div>
                </div>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Select Template</Form.Label>
                    <div className="patient-send-thankyou-modal__templates">
                        {THANK_YOU_TEMPLATES.map((template) => {
                            const isSelected = selectedTemplate === template.value;

                            return (
                                <button
                                    key={template.value}
                                    type="button"
                                    className={`patient-send-thankyou-modal__template-card${
                                        isSelected ? ' is-selected' : ''
                                    }`}
                                    onClick={() => {
                                        setSelectedTemplate(template.value);
                                        setPreviewHtml('');
                                        setPreviewMessage('');
                                        setPreviewSubject('');
                                        setShowPreview(false);
                                    }}
                                >
                                    <span className="patient-send-thankyou-modal__template-icon">
                                        <i className={`bi ${template.icon}`} />
                                    </span>
                                    <span className="patient-send-thankyou-modal__template-copy">
                                        <span className="patient-send-thankyou-modal__template-label">
                                            {template.label}
                                        </span>
                                        <span className="patient-send-thankyou-modal__template-description">
                                            {template.description}
                                        </span>
                                    </span>
                                    {isSelected && (
                                        <span className="patient-send-thankyou-modal__template-check">
                                            <i className="bi bi-check-circle-fill" />
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {selectedTemplateMeta && (
                        <Form.Text className="text-muted">
                            Preview and send using the <strong>{selectedTemplateMeta.label}</strong> template via{' '}
                            {isSms ? 'SMS' : 'email'}.
                        </Form.Text>
                    )}
                </Form.Group>

                {showPreview && (
                    <div className={isSms ? 'patient-send-sms-modal__preview' : 'patient-send-email-modal__preview'}>
                        {isSms ? (
                            <>
                                <h6 className="mb-3 fw-semibold">Preview</h6>
                                <div className="patient-send-sms-modal__phone">
                                    <div className="patient-send-sms-modal__phone-notch" />
                                    <div className="patient-send-sms-modal__phone-screen">
                                        <div className="patient-send-sms-modal__phone-header">
                                            <span className="patient-send-sms-modal__phone-contact">{patientLabel}</span>
                                            <span className="patient-send-sms-modal__phone-number">{recipientLabel}</span>
                                        </div>
                                        <div className="patient-send-sms-modal__phone-body">
                                            {previewLoading ? (
                                                <div className="patient-send-sms-modal__preview-loading">
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Generating preview...
                                                </div>
                                            ) : previewMessage ? (
                                                <div className="patient-send-sms-modal__message-bubble">
                                                    {previewMessage}
                                                </div>
                                            ) : (
                                                <p className="text-muted mb-0 small">No preview available.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="patient-send-email-modal__preview-header">
                                    <h6 className="mb-1 fw-semibold">Preview</h6>
                                    <span className="text-muted small">
                                        {previewSubject || 'Subject preview'}
                                    </span>
                                </div>

                                <div className="patient-send-email-modal__preview-body">
                                    {previewLoading ? (
                                        <div className="patient-send-email-modal__preview-loading">
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Generating preview...
                                        </div>
                                    ) : previewHtml ? (
                                        <div
                                            className="patient-send-email-modal__preview-html"
                                            dangerouslySetInnerHTML={{ __html: previewHtml }}
                                        />
                                    ) : (
                                        <p className="text-muted mb-0 small">No preview available.</p>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose} disabled={sending || previewLoading}>
                    Cancel
                </Button>
                <Button
                    variant="outline-primary"
                    type="button"
                    disabled={sending || previewLoading || !selectedTemplate}
                    onClick={handlePreview}
                >
                    {previewLoading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Previewing...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-eye me-1" />
                            Preview
                        </>
                    )}
                </Button>
                <Button
                    variant="primary"
                    type="button"
                    disabled={sending || previewLoading || !selectedTemplate}
                    onClick={handleSend}
                >
                    {sending ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Sending...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-send me-1" />
                            Send {isSms ? 'SMS' : 'Email'}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default SendPatientThankYouModal;
